import { compute as computeScrollIntoView } from 'compute-scroll-into-view';

import { NO_ELEMENT } from 'yeself-common/DomUtils';
import { ElementPadding } from 'yeself-common/typings/guide/LayerCommon';
import { DEFAULT_ELEMENT_PADDING } from 'yeself-common/ModelConstant';
import { ContainerDOMRect, ElementDOMRect } from 'yeself-common/typings';

/**
 * Returns true when provided `element` is in visible viewport.
 *
 * @param element Provided element.
 */
export const isElementInViewport = (element: Element): boolean => {
    const scroll = computeScrollIntoView(element, { scrollMode: 'if-needed', block: 'nearest' });

    return scroll.every(({ el, top, left }) => el.scrollTop === top && el.scrollLeft === left);
};

const CROP_MARGIN = 20;

export const isElementValid = (element: Element | undefined | null): element is HTMLElement =>
    !!element && element !== NO_ELEMENT && element.nodeType === Node.ELEMENT_NODE;

export const isElementDocumentFragment = (element: Element | HTMLElement | Node): boolean =>
    element.nodeType === Node.DOCUMENT_FRAGMENT_NODE;

export const elementParentElement = (
    element: Element | HTMLElement | Node
): { element: HTMLElement | null; isInShadowRoot: boolean } => {
    const { parentNode } = element;
    return parentNode && isElementDocumentFragment(parentNode)
        ? {
              element: (element as Node & { parentNode: { host: HTMLElement } }).parentNode.host,
              isInShadowRoot: true,
          }
        : { element: element.parentElement, isInShadowRoot: false };
};

/**
 * Returns true when `otherElement` is descendant of a given `element`.
 *
 * This method is limited on Internet Explorer, working only for HTMLElement and no other Nodes.
 *
 * @param element Given `element`.
 * @param otherElement
 */
export const isElementDescendantOfOtherElement = (
    element: Element | HTMLElement | Node,
    otherElement: Element | HTMLElement | Node
): boolean => element instanceof HTMLElement && element.contains(otherElement);

export const isElementInput = (element: Element): boolean => element.tagName === 'INPUT';

export const isElementSelect = (element: Element): boolean => element.tagName === 'SELECT';

export const isElementTextarea = (element: Element): boolean => element.tagName === 'TEXTAREA';

export const isElementFixedPosition = (element: Element): boolean => {
    let el: Element | null = element;

    while (el && isElementValid(el)) {
        const elementStyle = window.getComputedStyle(el);

        if (elementStyle.position === 'fixed' || elementStyle.position === 'sticky') {
            return true;
        }
        el = elementParentElement(el).element;
    }

    return false;
};

type DocumentIE = Document & {
    msElementsFromPoint: (x: number, y: number) => NodeListOf<Element>;
};

export const getAmountOfText = (element: HTMLElement): number =>
    ((element && element.innerText) || '').length;

/**
 * Returns an array of all elements at the given coordinates `x` and `y` (relative to the viewport) on given `target`.
 *
 * When element contains shadow root, methods is recursively recalled on it.
 *
 * @param x X coordinate.
 * @param y Y coordinate.
 * @param target Target on which elementsFromPoint is called.
 */
export const elementsFromPoint = (
    x: number,
    y: number,
    target: Document | DocumentIE | ShadowRoot = document
): Element[] => {
    const elementsList = (target as any).elementsFromPoint
        ? (target as Document).elementsFromPoint(x, y)
        : (target as DocumentIE).msElementsFromPoint(x, y);

    const elementsArr = Array.from(elementsList).reduceRight(
        (acc, element) => (acc.includes(element) ? acc : [element, ...acc]),
        [] as Element[]
    );

    if (elementsArr.length === 0) {
        return [];
    }

    const lastElementWithShadowRoot = elementsArr[0].shadowRoot
        ? elementsArr[0]
        : Array.from(elementsArr[0].children).find((element) => element.shadowRoot);

    return lastElementWithShadowRoot &&
        lastElementWithShadowRoot.shadowRoot &&
        (!(target instanceof ShadowRoot) || lastElementWithShadowRoot !== target.host)
        ? elementsFromPoint(x, y, lastElementWithShadowRoot.shadowRoot)
        : elementsArr;
};

export const viewportRect = (): ContainerDOMRect => {
    const { documentElement, body } = document;
    const top = window.pageYOffset || documentElement.scrollTop || body.scrollTop;
    const left = window.pageXOffset || documentElement.scrollLeft || body.scrollLeft;
    return {
        height: window.innerHeight,
        width: window.innerWidth,
        bottom: top + window.innerHeight,
        right: left + window.innerWidth,
        scrollTop: 0,
        scrollLeft: 0,
        scrollHeight: window.innerHeight,
        scrollWidth: window.innerWidth,
        x: left,
        y: top,
        top,
        left,
    };
};

export const bodyLeftAndTopOffset = (): { top: number; left: number } => {
    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    const scrollLeft = document.documentElement.scrollLeft || document.body.scrollLeft;

    const top = document.body.offsetTop - scrollTop;
    const left = document.body.offsetLeft - scrollLeft;

    return { top, left };
};

export const elementClientRect = (element: Element): ElementDOMRect => {
    const rect = element.getBoundingClientRect();
    return {
        top: rect.top,
        left: rect.left,
        bottom: rect.bottom,
        right: rect.right,
        height: rect.height,
        width: rect.width,
        x: rect.x,
        y: rect.y,
    };
};

export const elementDocumentRect = (element: Element): ElementDOMRect => {
    const clientRect = elementClientRect(element);
    const viewport = viewportRect();

    return {
        top: clientRect.top + viewport.top,
        left: clientRect.left + viewport.left,
        bottom: clientRect.bottom + viewport.top,
        right: clientRect.right + viewport.left,
        height: clientRect.height,
        width: clientRect.width,
        x: clientRect.left + viewport.left,
        y: clientRect.top + viewport.top,
    };
};

export const containerClientRect = (element: Element): ContainerDOMRect => {
    const rect = elementClientRect(element);
    return {
        ...rect,
        scrollTop: element.scrollTop,
        scrollLeft: element.scrollLeft,
        scrollHeight: element.scrollHeight,
        scrollWidth: element.scrollWidth,
    };
};

export const containerDocumentRect = (element: Element): ContainerDOMRect => {
    const rect = elementDocumentRect(element);
    return {
        ...rect,
        scrollTop: element.scrollTop,
        scrollLeft: element.scrollLeft,
        scrollHeight: element.scrollHeight,
        scrollWidth: element.scrollWidth,
    };
};

export const globalContainerRect = (
    documentRect: ContainerDOMRect,
    bodyRect: ContainerDOMRect,
    viewport: ContainerDOMRect
): ContainerDOMRect => {
    const scrollHeight = Math.max(
        documentRect.scrollHeight,
        bodyRect.scrollHeight,
        viewport.scrollHeight
    );
    const height = Math.max(documentRect.height, bodyRect.height, viewport.height, scrollHeight);

    return {
        ...documentRect,
        scrollHeight,
        height,
        bottom: Math.max(documentRect.bottom + documentRect.scrollTop, scrollHeight),
    };
};

export const containerRect = (element: Element, isFixed = false): ContainerDOMRect =>
    isFixed ? containerClientRect(element) : containerDocumentRect(element);

export const elementRect = (element: HTMLElement, isFixed = false): ElementDOMRect =>
    isFixed ? elementClientRect(element) : elementDocumentRect(element);

export const croppedToContentRect = (element: HTMLElement, isFixed = false): ElementDOMRect => {
    const rect = elementRect(element, isFixed);
    const nodes = element.querySelectorAll<HTMLElement>('*:not(option)');
    const leafNodes = Array.from(nodes).filter((node) => {
        const isNodeText =
            Array.from(node.childNodes).length > 0
                ? Array.from(node.childNodes).some((child) => child.nodeType === Node.TEXT_NODE) &&
                  node.innerText &&
                  node.innerText.trim()
                : node.innerText && node.innerText.trim();
        return (
            isElementInput(node) || isElementTextarea(node) || isElementSelect(node) || isNodeText || []
        );
    });
    const croppedRect = leafNodes.reduce(
        (acc, node) => {
            const nodeRect = elementRect(node, isFixed);
            return {
                right: Math.max(acc.right, nodeRect.right),
                bottom: Math.max(acc.bottom, nodeRect.bottom),
            };
        },
        { right: 0, bottom: 0 }
    );
    const bottom = Math.min(rect.bottom, croppedRect.bottom + CROP_MARGIN);
    const right = Math.min(rect.right, croppedRect.right + CROP_MARGIN);

    return {
        ...rect,
        right,
        bottom,
        height: bottom - rect.top,
        width: right - rect.left,
    };
};

export const rectWithPadding = (
    rect: ElementDOMRect,
    padding: ElementPadding = DEFAULT_ELEMENT_PADDING
): ElementDOMRect => ({
    top: rect.top - padding.top,
    left: rect.left - padding.left,
    right: rect.right + padding.right,
    bottom: rect.bottom + padding.bottom,
    height: rect.height + padding.top + padding.bottom,
    width: rect.width + padding.left + padding.right,
    x: rect.left - padding.left,
    y: rect.top - padding.top,
});

export const isElementStyleVisible = (element: Element): boolean => {
    const style = isElementValid(element) ? window.getComputedStyle(element) : false;
    return (
        style &&
        style.getPropertyValue('display') !== 'none' &&
        style.getPropertyValue('visibility') !== 'hidden'
    );
};

export const isElementPartiallyOutsideViewport = (
    element: Element,
    visibilityPercentage = 30
): boolean => {
    const childRect = elementClientRect(element);
    return childRect.bottom < (window.innerHeight / 100) * visibilityPercentage;
};

export const elementNthChild = (element: Element): number =>
    element !== document.body && element !== document.documentElement && element.parentNode
        ? Array.from(element.parentNode.childNodes)
              .filter((child) => child.nodeType === Node.ELEMENT_NODE)
              .indexOf(element) + 1
        : -1;

/**
 * Returns array of class names on the element.
 *
 * If the class name attribute is empty or does not exist, then empty array is returned.
 *
 * @param element Element from which the class names are returned.
 */
export const elementClassNameArray = (element: HTMLElement): string[] => {
    const elementClassName = element.getAttribute('class');
    const trimmedClassName = elementClassName ? elementClassName.trim() : '';

    return trimmedClassName ? trimmedClassName.split(' ').map((className) => className.trim()) : [];
};

export const getElementScrollParent = (element: Element): Element => {
    let parent = elementParentElement(element).element;
    let childElement = element;

    while (parent && parent !== document.documentElement) {
        const verticalScroll = parent.clientHeight > 0 && parent.scrollHeight > parent.clientHeight;
        const horizontalScroll = parent.clientWidth > 0 && parent.scrollWidth > parent.clientWidth;

        if (horizontalScroll) {
            if (parent.offsetHeight > parent.clientHeight) {
                return childElement;
            }
        }

        if (verticalScroll) {
            if (parent.offsetWidth > parent.clientWidth) {
                return childElement;
            }
        }

        childElement = parent;
        parent = elementParentElement(childElement).element;
    }

    return document.documentElement;
};

export const getElementDocumentFragments = (element: Element): Node[] => {
    let parent = elementParentElement(element).element;
    let childElement = element;
    let fragments: Node[] = [];

    while (parent && parent !== document.documentElement) {
        const { parentNode } = parent;
        if (parentNode && isElementDocumentFragment(parentNode)) {
            fragments = [...fragments, parentNode];
        }

        childElement = parent;
        parent = elementParentElement(childElement).element;
    }

    return fragments;
};

/**
 * Returns array of elements that serves as host for document fragment from given `element`.
 *
 * If none element is host for document fragment, empty array is returned.
 *
 * @param element Element from which the document fragments are retrieved.
 */
export const getAllDocumentFragmentsInElement = (element: HTMLElement): HTMLElement[] => {
    const allElements = document.evaluate(
        '/descendant-or-self::node()',
        element,
        null,
        XPathResult.ORDERED_NODE_ITERATOR_TYPE
    );

    let fragments: HTMLElement[] = [];

    while (true) {
        const node = allElements.iterateNext();

        if (node) {
            fragments =
                node instanceof HTMLElement && node.shadowRoot ? [...fragments, node] : fragments;
        } else {
            return fragments;
        }
    }
};


export const getHighestZIndex = (initialElement: Element): number => {
    let highestZIndex = window.getComputedStyle(initialElement).getPropertyValue('z-index');
    let element = initialElement;

    while (element.parentElement) {
        const computedStyles = window.getComputedStyle(element);
        const zIndex = computedStyles.getPropertyValue('z-index');
        const zIndexValue = parseInt(zIndex, 10);
        if (!Number.isNaN(zIndexValue) && zIndexValue > parseInt(highestZIndex, 10)) {
            highestZIndex = zIndex;
        }
        element = element.parentElement;
    }
    return highestZIndex === 'auto' ? 0 : parseInt(highestZIndex, 10);
};

